import type { ReactNode } from "react";
import { ArrowDown, Database, FileCheck2, Globe2, KeyRound, Lock, Plug, ScrollText, ShieldCheck, ShieldOff } from "lucide-react";
import { Card } from "../../components/ui";

const LAYERS: [ReactNode, string, string][] = [
  [<Database className="size-4" />, "Private database", "Stays in your network. Never publicly exposed by DBIndex."],
  [<Plug className="size-4" />, "DBIndex Connector", "Outbound-only, read-only account, no inbound ports, no arbitrary commands."],
  [<ShieldCheck className="size-4" />, "Sanitisation policy", "Signed exposure contract enforced locally before data leaves."],
  [<Lock className="size-4" />, "Encrypted sync", "TLS everywhere; short-lived session tokens bound to a key only the connector holds."],
  [<FileCheck2 className="size-4" />, "Serving projection", "Only approved fields exist. Validation re-checks every batch."],
  [<Globe2 className="size-4" />, "Global network", "Typed query plans only; results carry provenance and licence."],
];

export function Security() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <h1 className="text-3xl font-semibold tracking-tight">Security</h1>
      <p className="mt-2 max-w-2xl text-fg-2">DBIndex assumes hostile traffic and hostile data. The architecture is designed so that the serving layer cannot contain what was never approved.</p>
      <div className="mt-10 grid gap-10 lg:grid-cols-[380px_1fr]">
        <ol className="space-y-1" aria-label="Data path">
          {LAYERS.map(([icon, h, d], i) => (
            <li key={h}>
              <div className="flex gap-3 rounded-lg border border-border bg-panel p-4">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent" aria-hidden>
                  {icon}
                </span>
                <div>
                  <div className="text-sm font-medium">{h}</div>
                  <div className="mt-0.5 text-xs text-fg-2">{d}</div>
                </div>
              </div>
              {i < LAYERS.length - 1 && <ArrowDown className="mx-auto my-1 size-4 text-fg-3" aria-hidden />}
            </li>
          ))}
        </ol>
        <div className="space-y-6">
          <Block id="contracts" icon={<FileCheck2 className="size-4" />} title="Data Exposure Contracts">
            Every exposure is a versioned, hash-chained, Ed25519-signed contract. It lists each source column as Shared, Transformed (hash, tokenise, redact, generalise, bucket, round…) or Blocked. Authentication secrets and prohibited data can never be exported — not even with an override. Changing a contract produces a diff and a migration plan and requires re-approval.
          </Block>
          <Block id="privacy" icon={<ShieldOff className="size-4" />} title="Privacy by design">
            Sensitive classes are default-deny. New source columns arrive blocked until classified and approved. "What leaves my network?" returns an exact lineage report: source → transformation → serving field → capability. Aggregates enforce minimum group sizes.
          </Block>
          <Block icon={<KeyRound className="size-4" />} title="Credentials">
            Source database credentials remain inside your network with the connector. DBIndex stores only the connector's public key; API secrets are shown once and stored as hashes; credentials never appear in URLs, logs or analytics.
          </Block>
          <Block icon={<Lock className="size-4" />} title="No arbitrary SQL, no prompt injection path">
            LLMs may only emit a typed query plan validated against approved capabilities. Identifiers come from the contract; values are bound parameters; every query has a row limit and timeout. Retrieved database text is treated as untrusted data, never as instructions.
          </Block>
          <Block icon={<ScrollText className="size-4" />} title="Auditability and tenancy">
            Connections, classifications, overrides, approvals, activations, price changes and key events create immutable audit records. Tenant identity is resolved server-side on every request, and cache scopes (public, dataset, consumer) prevent cross-tenant result reuse.
          </Block>
        </div>
      </div>
    </div>
  );
}

function Block({ id, icon, title, children }: { id?: string; icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <Card as="article" className="p-6">
      <h2 id={id} className="flex scroll-mt-20 items-center gap-2 text-base font-semibold">
        <span className="text-accent" aria-hidden>
          {icon}
        </span>
        {title}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-fg-2">{children}</p>
    </Card>
  );
}
